import { Clamp, LocalStorageUtils } from '@nova/utils';
import { sound } from '@pixi/sound';

import { BGM } from './BGMManager';
import { SFX } from './SFXManager';

// LocalStorage schema for audio settings
export type AudioSettings = {
  music: {
    volume: number;
    mute: boolean;
  };
  sfx: {
    volume: number;
    mute: boolean;
  };
};

const DEFAULT_AUDIO_SETTINGS = {
  music: {
    volume: 0.5,
    mute: false
  },
  sfx: {
    volume: 0.5,
    mute: false
  }
};

export enum AudioType {
  music = 'music',
  sfx = 'sfx'
}

/**
 * AudioManager
 *
 * Handles the main audio systems in the game.
 * Initializes the BGM and SFX managers as well as manages user audio settings.
 */
export class AudioManager {
  public static BGM: BGM;
  public static SFX: SFX;

  private static readonly _storageID: string = 'InGameAudioSettings';

  private static _audioSettings: AudioSettings;
  private static _visibilityChangeHandler: (() => void) | null = null;
  private static _focusHandler: (() => void) | null = null;
  private static _blurHandler: (() => void) | null = null;

  /**
   * Gets the global volume of the game
   */
  public static get masterVolume(): number {
    return sound.volumeAll;
  }

  /**
   * Sets the global volume of the game
   */
  public static set masterVolume(volume: number) {
    sound.volumeAll = Clamp(volume, 0, 1);
  }

  public static get audioSettings(): AudioSettings {
    return this._audioSettings;
  }

  public static initialize(): void {
    sound.disableAutoPause = true;
    this.loadSettings();

    this.BGM = new BGM();
    this.SFX = new SFX();

    // Store bound handler references so they can be properly removed later
    this._visibilityChangeHandler = this.onVisibilitychange.bind(this);
    this._focusHandler = this.onFocus.bind(this);
    this._blurHandler = this.onBlur.bind(this);

    // Mute the game when it is not in focus or not visible to the user.
    document.addEventListener('visibilitychange', this._visibilityChangeHandler);
    globalThis.addEventListener('focus', this._focusHandler);
    globalThis.addEventListener('blur', this._blurHandler);
  }

  private static onVisibilitychange(): void {
    if (document.visibilityState !== 'visible') {
      sound.muteAll();
    }
    else {
      sound.unmuteAll();
    }
  }

  private static onFocus(): void {
    sound.unmuteAll();
  }

  private static onBlur(): void {
    sound.muteAll();
  }

  /**
    * Load audio settings from localStorage. If the settings do not exist then defaults are applied.
    */
  private static loadSettings(): void {
    this._audioSettings = LocalStorageUtils.getLocalStorage(this._storageID) as AudioSettings;
    if (!this._audioSettings) {
      this._audioSettings = DEFAULT_AUDIO_SETTINGS;
      LocalStorageUtils.setLocalStorage(this._storageID, this._audioSettings);
    }
  }

  /**
   * Save current audio settings to local storage.
   */
  public static saveSettings(): void {
    LocalStorageUtils.setLocalStorage(this._storageID, this._audioSettings);
  }

  /**
   * Mute all sounds in the game
   */
  public static mute(): void {
    sound.muteAll();
  }

  /**
   * Unmute all sounds in the game
   */
  public static unmute(): void {
    sound.unmuteAll();
  }

  public static stop(): void {
    AudioManager.BGM.stop();
    AudioManager.SFX.stop();
  }

  public static pause(): void {
    AudioManager.BGM.pause();
    AudioManager.SFX.pause();
  }

  public static resume(): void {
    AudioManager.BGM.resume();
    AudioManager.SFX.resume();
  }

  /**
   * Destroys the AudioManager and cleans up all audio systems.
   */
  public static destroy(): void {
    // Stop all audio
    this.stop();

    // Remove event listeners first to prevent access to undefined settings
    if (this._visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
      this._visibilityChangeHandler = null;
    }

    if (this._focusHandler) {
      globalThis.removeEventListener('focus', this._focusHandler);
      this._focusHandler = null;
    }

    if (this._blurHandler) {
      globalThis.removeEventListener('blur', this._blurHandler);
      this._blurHandler = null;
    }

    // Destroy BGM and SFX managers
    if (this.BGM) {
      this.BGM.destroy();
      this.BGM = undefined as never;
    }

    if (this.SFX) {
      this.SFX.destroy();
      this.SFX = undefined as never;
    }

    // Reset settings last
    this._audioSettings = undefined as never;
  }
}
